import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap'
import DataTable from 'react-data-table-component'
import { FaCheckCircle, FaEye, FaSearch, FaSync } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import TransaksiService from '../../../services/transaksi/TransaksiService'
import UserService from '../../../services/user/UserService'
import Negara from '../../../utilities/Negara'
import UPT from '../../../utilities/UPT'
import { formatDateTimeLocalDate } from '../../../utilities/Utils'

const SiapApprove = () => {
    const navigate = useNavigate()
    const transaksiService = useMemo(() => new TransaksiService(), [])
    const userService = useMemo(() => new UserService(), [])
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [searchAju, setSearchAju] = useState('')
    const [page, setPage] = useState(0)
    const [size, setSize] = useState(10)
    const [total, setTotal] = useState(0)
    const [error, setError] = useState('')

    const load = useCallback(async (pageNumber = 0, pageSize = size, search = searchAju) => {
        setLoading(true)
        setError('')
        try {
            const kodeTrader = localStorage.getItem('kodeTrader')
            if (!kodeTrader) {
                setRows([])
                setTotal(0)
                return
            }
            const trader = await userService.getTraderById(kodeTrader)
            const response = await transaksiService.listPpm(
                search || null,
                trader.data.npwp,
                '027',
                pageNumber,
                pageSize
            )
            setRows(response?.data?.data || [])
            setTotal(Number(response?.data?.totalData || 0))
            setPage(pageNumber)
            setSize(pageSize)
        } catch (e) {
            setRows([])
            setTotal(0)
            setError('Daftar dokumen yang siap disetujui tidak dapat dimuat.')
        } finally {
            setLoading(false)
        }
    }, [searchAju, size, transaksiService, userService])

    useEffect(() => {
        load(0, size, '')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const columns = [
        { name: 'Nomor Aju', selector: row => row.nomorAju, sortable: true, grow: 0.8 },
        { name: 'Tanggal Aju', cell: row => formatDateTimeLocalDate(row.tanggalAju), grow: 0.7 },
        { name: 'UPT', cell: row => <>{row.kdUpt} - <UPT id={row.kdUpt} /></>, grow: 1 },
        { name: 'Nama Pengguna Jasa', selector: row => row.namaTrader, grow: 1.2 },
        { name: 'Tujuan', cell: row => <Negara id={row.negaraPartner} />, grow: 0.8 },
        {
            name: 'Action',
            cell: row => (
                <div className="d-flex flex-column gap-1 py-1">
                    <Button size="sm" variant="primary" onClick={() => navigate(`/smkhp/approval/${encodeURIComponent(row.nomorAju)}`)}>
                        <FaCheckCircle className="me-1" /> Periksa & Setujui
                    </Button>
                    <Button size="sm" variant="outline-info" onClick={() => {
                        localStorage.setItem('nomorAjuStorage', row.nomorAju)
                        localStorage.setItem('isEditStorage', '1')
                        navigate('/permohonan/ekspor/view-2')
                    }}>
                        <FaEye className="me-1" /> Lihat Data
                    </Button>
                </div>
            ),
            grow: 1.1,
            minWidth: '180px'
        }
    ]

    const search = () => load(0, size, searchAju)
    const reset = () => {
        setSearchAju('')
        load(0, size, '')
    }

    return (
        <div className="container-fluid py-3">
            <Row>
                <Col sm="12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <div className="header-title">
                                <h4 className="card-title mb-0">Permohonan Siap Disetujui</h4>
                                <small className="text-muted">Draft PDF SMKHP yang menunggu persetujuan Pengguna Jasa</small>
                            </div>
                            <Button size="sm" variant="outline-primary" onClick={() => load(page, size, searchAju)} disabled={loading}>
                                <FaSync className="me-1" /> Muat Ulang
                            </Button>
                        </Card.Header>
                        <Card.Body>
                            <Alert variant="info">
                                Periksa draft PDF SMKHP terlebih dahulu. Jika ada data yang belum benar, gunakan menu <strong>Ubah Data Permohonan</strong> pada halaman persetujuan sebelum memberikan persetujuan.
                            </Alert>
                            <Row className="align-items-end">
                                <Col md="5">
                                    <Form.Group>
                                        <Form.Label>Nomor Aju</Form.Label>
                                        <Form.Control
                                            size="sm"
                                            type="search"
                                            placeholder="Cari Nomor Aju"
                                            value={searchAju}
                                            onChange={e => setSearchAju(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && search()}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md="7" className="mt-2 mt-md-0">
                                    <Button size="sm" variant="primary" onClick={search} disabled={loading}>
                                        <FaSearch className="me-1" /> Cari
                                    </Button>{' '}
                                    <Button size="sm" variant="secondary" onClick={reset} disabled={loading}>Reset</Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Header>
                            <h4 className="card-title mb-0">Daftar Draft SMKHP Menunggu Persetujuan</h4>
                        </Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger">{error}</Alert>}
                            {loading && <div className="text-center py-3"><Spinner animation="border" /></div>}
                            <DataTable
                                customStyles={{ headCells: { style: { backgroundColor: '#00acee', color: 'white' } } }}
                                columns={columns}
                                data={rows}
                                highlightOnHover
                                pagination
                                paginationServer
                                paginationTotalRows={total}
                                paginationDefaultPage={page + 1}
                                onChangePage={(nextPage) => load(nextPage - 1, size, searchAju)}
                                onChangeRowsPerPage={(nextSize, nextPage) => load(nextPage - 1, nextSize, searchAju)}
                                persistTableHead
                                noDataComponent="Tidak ada draft SMKHP yang menunggu persetujuan."
                            />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}

export default SiapApprove
